import type { Key } from "react-aria-components";

import { RequestStatusEnum } from "@nlc/shared-types";
import { ChevronDown, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import {
  Select,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { STATUS_OPTIONS } from "@/features/shared/constants/statusColors";

const SELECTABLE_STATUS_OPTIONS = STATUS_OPTIONS.filter(
  (option) => option.id !== RequestStatusEnum.Completed,
);

interface BulkChangeStatusDropdownProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  onStatusSelect: (statusId: number, statusLabel: string) => void;
}

export function BulkChangeStatusDropdown({
  isDisabled = false,
  isLoading = false,
  onStatusSelect,
}: BulkChangeStatusDropdownProps) {
  const [selectedKey, setSelectedKey] = useState<Key | null>(null);

  const handleSelectionChange = (key: Key | null) => {
    if (key === null) return;
    setSelectedKey(key);

    const selectedStatus = SELECTABLE_STATUS_OPTIONS.find(
      (status) => status.id === Number(key),
    );

    if (selectedStatus) {
      onStatusSelect(selectedStatus.id, selectedStatus.label);
      // Reset selection after callback
      setSelectedKey(null);
    }
  };

  return (
    <Select
      aria-label="Change status"
      isDisabled={isDisabled || isLoading}
      onSelectionChange={handleSelectionChange}
      selectedKey={selectedKey}
    >
      <Button
        className="flex items-center gap-2 bg-button-secondary-bg px-3 py-1.5 text-sm rounded-md border shadow-sm transition-colors border-border-primary text-button-secondary-fg hover:bg-button-secondary-bg-hover disabled:border-border-secondary disabled:text-fg-disabled disabled:cursor-not-allowed disabled:hover:bg-button-secondary-bg min-w-[140px]"
        variant="unstyled"
      >
        <RefreshCw
          className="shrink-0"
          height={16}
          strokeWidth={2}
          width={16}
        />
        <span className="font-semibold">Change status</span>
        <ChevronDown
          className="shrink-0"
          height={16}
          strokeWidth={2}
          width={16}
        />
      </Button>
      <SelectPopover className="w-[--trigger-width] min-w-[200px]">
        <SelectListBox>
          {SELECTABLE_STATUS_OPTIONS.map(({ id, label, variant }) => (
            <SelectListItem id={id} key={id} textValue={label}>
              <Badge isRounded size="small" variant={variant}>
                {label}
              </Badge>
            </SelectListItem>
          ))}
        </SelectListBox>
      </SelectPopover>
    </Select>
  );
}
